import { nameSchema } from '@argus-labs/auth-shared/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '@/components/ui/button'
import { ClearableInput } from '@/components/ui/clearable-input'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { PageCard } from '@/components/ui/page-card'
import { authClient } from '@/lib/auth-client'
import { getSessionQueryOptions, invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/_auth/edit/name')({
  component: EditName,
})

function EditName() {
  return (
    <PageCard title="Edit Full Name">
      <EditNameForm />
    </PageCard>
  )
}

const editNameFormSchema = nameSchema
type EditNameFormParams = z.infer<typeof editNameFormSchema>

export function EditNameForm() {
  const {
    data: { data },
  } = useSuspenseQuery(getSessionQueryOptions())
  const name = data?.user.name
  const form = useForm<EditNameFormParams>({
    resolver: zodResolver(editNameFormSchema),
    defaultValues: { name },
  })
  const navigate = useNavigate()

  async function onSubmit(values: EditNameFormParams) {
    try {
      const { data, error } = await authClient.updateUser({
        name: values.name,
      })

      if (data) {
        invalidateSessionQuery()

        return navigate({ to: '/account' })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <Controller
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <FieldLabel htmlFor="edit-name-name">Full Name*</FieldLabel>
              <ClearableInput
                {...field}
                id="edit-name-name"
                type="text"
                onClear={() => field.onChange('')}
                required
                autoFocus
                autoComplete="name"
                placeholder="eg: mojo doe"
                aria-invalid={fieldState.invalid}
                aria-describedby={fieldState.invalid ? 'edit-name-name-error' : undefined}
              />
              <FieldError id="edit-name-name-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
          Update
        </Button>
      </FieldGroup>
    </form>
  )
}
